import React from "react";
import PageShell from "./PageShell";
import { useLocation } from "react-router-dom";
import { navMenu, services, products, platforms, boardMembers, branches, announcements, bulletins, trainings } from "../mock";
import { TrendingUp, BarChart3, FileText, Briefcase, Building2, Users, Calendar, Tag, Clock, GraduationCap, ChevronRight, MapPin, Mail, Award, Target, Eye, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { SRETRADE_LOGIN, SRETRADE_REGISTER } from "../mock";

const iconMap = { TrendingUp, BarChart3, FileText, Briefcase, Building2, Users };

// Helper to find label from slug
const findLabel = (slug) => {
  for (const m of navMenu) {
    if (m.slug === slug) return { label: m.label, parent: null };
    if (m.children) {
      const c = m.children.find((c) => c.slug === slug);
      if (c) return { label: c.label, parent: { label: m.label, slug: m.slug } };
    }
  }
  return { label: "Sayfa", parent: null };
};

const GenericPage = () => {
  const location = useLocation();
  const { label, parent } = findLabel(location.pathname);
  const breadcrumbs = parent ? [parent, { label }] : [{ label }];

  const content = getContent(location.pathname);

  return (
    <PageShell title={label} subtitle={content.subtitle} breadcrumbs={breadcrumbs}>
      {content.body}
    </PageShell>
  );
};

// Content map for various routes
function getContent(path) {
  // Hakkımızda
  if (path.startsWith("/hakkimizda")) {
    if (path.endsWith("yonetim-kurulu")) {
      return {
        subtitle: "Acar Menkul Değerler A.Ş. Yönetim Kurulu üyeleri",
        body: (
          <div className="grid sm:grid-cols-2 gap-5">
            {boardMembers.map((m, i) => (
              <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1c4d9c] to-[#143a78] text-white flex items-center justify-center font-bold text-xl">
                  {m.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{m.name}</p>
                  <p className="text-sm text-slate-500">{m.title}</p>
                </div>
              </div>
            ))}
          </div>
        ),
      };
    }
    if (path.endsWith("vizyon-misyon")) {
      return {
        subtitle: "Vizyon, misyon ve değerlerimiz",
        body: (
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, t: "Vizyonumuz", d: "Türkiye'nin lider aracı kurumları arasında yer almak ve yatırımcıların ilk tercihi olmak." },
              { icon: Target, t: "Misyonumuz", d: "Müşterilerimize en kaliteli aracılık ve danışmanlık hizmetlerini en uygun koşullarda sunmak." },
              { icon: Heart, t: "Değerlerimiz", d: "Güven, şeffaflık, profesyonellik, müşteri odaklılık ve sürekli gelişim." },
            ].map((c, i) => (
              <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-7">
                <div className="w-12 h-12 rounded-md bg-[#1c4d9c]/10 text-[#1c4d9c] flex items-center justify-center mb-4">
                  <c.icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">{c.t}</h3>
                <p className="text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        ),
      };
    }
    if (path.endsWith("bilgi-almak-istiyorum")) {
      return {
        subtitle: "Bizimle iletişime geçin, size en uygun yatırım hesabını birlikte seçelim.",
        body: <ContactForm />,
      };
    }
    // Default Hakkımızda
    return {
      subtitle: "37 yıllık tecrübe ve birikimle hizmetinizdeyiz.",
      body: (
        <div className="prose max-w-none">
          <p className="text-slate-700 leading-relaxed mb-5">
            Acar Menkul Değerler A.Ş., 1988 yılında İstanbul'da kurulmuş ve
            Sermaye Piyasası Kurulu lisanslı bir aracı kurumdur. 37 yıllık
            tecrübesi ve uzman kadrosu ile yatırımcılarına hisse senedi, VİOP,
            tahvil-bono, yatırım fonu ve halka arz hizmetleri sunmaktadır.
          </p>
          <p className="text-slate-700 leading-relaxed mb-5">
            Borsa İstanbul ve Vadeli İşlem Piyasası'nın yetkili aracı kurumu
            olarak müşterilerimize anlık veri, gelişmiş işlem platformları ve
            ücretsiz eğitimlerle birlikte uygun komisyon oranları sunuyoruz.
          </p>
          <div className="grid sm:grid-cols-3 gap-5 mt-8">
            {[
              { n: "37+", l: "Yıl Tecrübe" },
              { n: "50.000+", l: "Aktif Yatırımcı" },
              { n: "3", l: "Şube" },
            ].map((s, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                <div className="text-3xl font-bold text-[#1c4d9c]">{s.n}</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    };
  }

  if (path.startsWith("/hizmetlerimiz")) {
    return {
      subtitle: "Yatırımcılarımıza sunduğumuz aracılık ve danışmanlık hizmetleri.",
      body: (
        <div className="grid sm:grid-cols-2 gap-5">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || TrendingUp;
            return (
              <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6">
                <div className="w-12 h-12 rounded-md bg-[#1c4d9c]/10 text-[#1c4d9c] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.description}</p>
              </div>
            );
          })}
        </div>
      ),
    };
  }

  if (path.startsWith("/yatirim-urunleri")) {
    return {
      subtitle: "Geniş yatırım ürün yelpazemizle her yatırımcıya özel çözümler.",
      body: (
        <div className="space-y-5">
          {products.map((p, i) => (
            <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-bold text-lg text-[#1c4d9c] mb-2">{p.title}</h3>
              <p className="text-slate-600 mb-4">{p.description}</p>
              <div className="flex flex-wrap gap-2">
                {p.features.map((f, j) => (
                  <span key={j} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    };
  }

  if (path.startsWith("/platformlarimiz")) {
    return {
      subtitle: "Modern teknoloji ile geliştirilmiş işlem platformlarımız.",
      body: (
        <div className="grid sm:grid-cols-2 gap-5">
          {platforms.map((p, i) => (
            <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6 relative overflow-hidden">
              <span className="absolute top-4 right-4 px-2.5 py-1 rounded text-[11px] font-bold bg-[#1c4d9c]/10 text-[#1c4d9c]">
                {p.badge}
              </span>
              <h3 className="font-bold text-lg text-slate-800 mb-2">{p.title}</h3>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">{p.description}</p>
              <ul className="space-y-1.5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-slate-700">
                    <ChevronRight className="w-3.5 h-3.5 text-[#1c4d9c]" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href={SRETRADE_LOGIN}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-block text-[#1c4d9c] hover:underline font-semibold text-sm"
              >
                Giriş Yap →
              </a>
            </div>
          ))}
        </div>
      ),
    };
  }

  if (path.startsWith("/arastirma")) {
    return {
      subtitle: "Araştırma ekibimizden güncel piyasa raporları ve analizler.",
      body: (
        <div className="space-y-3">
          {bulletins.map((b) => (
            <div key={b.id} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded bg-[#1c4d9c]/10 text-[#1c4d9c] flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h3 className="font-semibold text-slate-800">{b.title}</h3>
                  <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#1c4d9c]/10 text-[#1c4d9c]">
                    {b.type}
                  </span>
                </div>
                <p className="text-slate-600 text-sm mb-2">{b.description}</p>
                <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                  <Calendar className="w-3.5 h-3.5" /> {b.date}
                </div>
              </div>
              <button className="text-[#1c4d9c] hover:underline text-sm font-medium">
                İndir
              </button>
            </div>
          ))}
        </div>
      ),
    };
  }

  if (path.startsWith("/duyurular")) {
    return {
      subtitle: "Şirketimiz ve piyasalara dair güncel duyurular.",
      body: (
        <div className="space-y-4">
          {announcements.map((a) => (
            <div key={a.id} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                <Tag className="w-3.5 h-3.5" /> {a.category}
                <span>·</span>
                <Calendar className="w-3.5 h-3.5" /> {a.date}
              </div>
              <h3 className="font-semibold text-slate-800 mb-2 text-lg">{a.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{a.excerpt}</p>
              <button className="mt-3 text-[#1c4d9c] hover:underline text-sm font-medium">
                Devamını Oku →
              </button>
            </div>
          ))}
        </div>
      ),
    };
  }

  if (path.startsWith("/egitimlerimiz")) {
    return {
      subtitle: "Ücretsiz eğitim programlarımız ile yatırım bilginizi geliştirin.",
      body: (
        <div className="grid sm:grid-cols-2 gap-5">
          {trainings.map((t) => (
            <div key={t.id} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6">
              <div className="w-12 h-12 rounded-md bg-[#1c4d9c]/10 text-[#1c4d9c] flex items-center justify-center mb-4">
                <GraduationCap className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-1">{t.title}</h3>
              <p className="text-sm text-slate-500 mb-3">{t.instructor}</p>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">{t.description}</p>
              <div className="flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t.duration}</span>
                <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" /> {t.level}</span>
              </div>
            </div>
          ))}
        </div>
      ),
    };
  }

  if (path.startsWith("/iletisim")) {
    return {
      subtitle: "Bize ulaşın, sorularınızı yanıtlamaktan memnuniyet duyarız.",
      body: (
        <div className="grid lg:grid-cols-3 gap-5">
          {branches.map((b, i) => (
            <div key={i} className="lift-on-hover bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-bold text-lg text-[#1c4d9c] mb-4">{b.name}</h3>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#1c4d9c] shrink-0" /> {b.address}</li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#1c4d9c]" /> <a href={`mailto:${b.email}`} className="hover:text-[#1c4d9c]">{b.email}</a></li>
              </ul>
            </div>
          ))}
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      ),
    };
  }

  if (path === "/kisisel-verilerin-korunmasi") {
    return {
      subtitle: "KVKK Aydınlatma Metni",
      body: (
        <div className="prose max-w-none text-slate-700 leading-relaxed space-y-4">
          <p>
            Acar Menkul Değerler A.Ş. olarak, 6698 sayılı Kişisel Verilerin
            Korunması Kanunu kapsamında kişisel verilerinizin güvenliğine
            önem vermekteyiz. Bu metin, hangi verilerinizin hangi amaçla
            işlendiği konusunda sizi bilgilendirmek amacıyla hazırlanmıştır.
          </p>
          <h3 className="text-lg font-semibold text-slate-800">İşlenen Veri Kategorileri</h3>
          <p>Kimlik bilgileri, iletişim bilgileri, finansal bilgiler, işlem güvenliği bilgileri ve risk yönetimi bilgileri.</p>
          <h3 className="text-lg font-semibold text-slate-800">Verilerin İşlenme Amaçları</h3>
          <p>Sermaye piyasası faaliyetlerinin yürütülmesi, mevzuat ve yükümlülüklerin yerine getirilmesi, müşteri ilişkilerinin yönetilmesi.</p>
          <h3 className="text-lg font-semibold text-slate-800">Haklarınız</h3>
          <p>KVKK’nın 11. maddesi kapsamında bilgi talep etme, düzeltme isteme, silinmesini talep etme gibi haklarınız bulunmaktadır.</p>
        </div>
      ),
    };
  }

  if (path === "/bilgi-toplum-hizmeti" || path === "/acil-durum-plani") {
    return {
      subtitle: path.includes("bilgi")
        ? "Şirketimiz hakkında bilgi toplum hizmeti kapsamında yayınlanan dökümanlar."
        : "Acil ve beklenmedik durumlar için hazırlanmış planımız.",
      body: (
        <div className="prose max-w-none text-slate-700 leading-relaxed space-y-4">
          <p>
            Bu sayfada şirketimize ait yasal yayınlar ve raporlara
            ulaşabilirsiniz. Tüm raporlar SPK ve KAP düzenlemelerine uygun
            olarak hazırlanmaktadır.
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Yıllık Faaliyet Raporu</li>
            <li>Finansal Tablolar</li>
            <li>Ortaklık Yapısı</li>
            <li>Esas Sözleşme</li>
            <li>Genel Kurul Bilgilendirme Dökümanları</li>
            <li>Acil Durum Planı</li>
          </ul>
        </div>
      ),
    };
  }

  return {
    subtitle: "Sayfa içeriği yakında eklenecektir.",
    body: (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-10 text-center">
        <p className="text-slate-600">Bu sayfanın içeriği hazırlanmaktadır.</p>
        <Link to="/" className="inline-block mt-5 text-[#1c4d9c] hover:underline font-semibold">
          Anasayfaya Dön
        </Link>
      </div>
    ),
  };
}

const ContactForm = () => {
  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Mesajınız alınmıştır. En kısa sürede dönüş sağlanacaktır.");
    e.target.reset();
  };
  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-200 rounded-lg p-6 grid sm:grid-cols-2 gap-4">
      <input required type="text" placeholder="Adınız" className="px-4 py-3 bg-white rounded-md border border-slate-200 focus:border-[#1c4d9c] transition" />
      <input required type="text" placeholder="Soyadınız" className="px-4 py-3 bg-white rounded-md border border-slate-200 focus:border-[#1c4d9c] transition" />
      <input required type="tel" placeholder="Telefon" className="px-4 py-3 bg-white rounded-md border border-slate-200 focus:border-[#1c4d9c] transition" />
      <input required type="email" placeholder="E-Posta" className="px-4 py-3 bg-white rounded-md border border-slate-200 focus:border-[#1c4d9c] transition" />
      <textarea required placeholder="Mesajınız" rows="4" className="sm:col-span-2 px-4 py-3 bg-white rounded-md border border-slate-200 focus:border-[#1c4d9c] transition" />
      <button type="submit" className="sm:col-span-2 bg-[#1c4d9c] hover:bg-[#143a78] text-white font-semibold py-3 rounded-md transition">
        Gönder
      </button>
    </form>
  );
};

export default GenericPage;
